// =============================================================================
// Stale-chunk auto-recovery
//
// An SPA shell loaded *before* a deploy holds a dynamic-import map pointing at
// chunk hashes the new deploy deleted. Because routes like /account/playground
// are `lazy()`, the stale hash isn't requested until the user navigates there —
// by then the file is gone. Netlify's SPA catch-all serves index.html (200,
// text/html) for the missing `*.js`, the browser rejects it on the module MIME
// check, the dynamic import throws, and the page goes blank.
//
// Recovery is a single full reload: fetching the current index.html pulls the
// fresh import map + live chunk hashes. Vite fires a `vite:preloadError` event
// for this exact case; we also listen for the raw rejection/error because the
// rolldown runtime doesn't always emit the Vite event.
//
// A short-lived sessionStorage stamp guards against a reload loop: if the
// failure is NOT staleness (a genuinely broken deploy), we reload once, then
// stop and let the error surface instead of reload-storming.
// =============================================================================

const RELOAD_FLAG = 'gb:chunk-reload-at'
const RELOAD_WINDOW_MS = 10_000

// Messages browsers use when a dynamically-imported module fails to load.
const DYNAMIC_IMPORT_RE =
  /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|expected a javascript.{0,40}module script/i

/**
 * True when a message looks like a failed dynamic-import / stale-chunk load
 * (the class of error a post-deploy reload can recover from). Pure — exported
 * for tests.
 */
export function isChunkLoadErrorMessage(message: unknown): boolean {
  return typeof message === 'string' && DYNAMIC_IMPORT_RE.test(message)
}

/**
 * Loop guard: true when a reload was already stamped within the window, so we
 * must NOT reload again (the failure isn't staleness — a genuinely broken
 * deploy — and reloading would storm). Pure — exported for tests.
 */
export function isReloadWithinWindow(
  lastStampRaw: string | null,
  now: number,
  windowMs = RELOAD_WINDOW_MS,
): boolean {
  if (!lastStampRaw) return false
  const at = Number(lastStampRaw)
  return Number.isFinite(at) && now - at >= 0 && now - at < windowMs
}

function recentlyReloaded(): boolean {
  try {
    return isReloadWithinWindow(sessionStorage.getItem(RELOAD_FLAG), Date.now())
  } catch {
    return false
  }
}

/**
 * Reload to the fresh shell unless we already reloaded within the guard window
 * (a genuinely broken deploy, not just a stale chunk). Returns true when a
 * reload was initiated — callers use this to decide whether to suppress the
 * underlying error: suppress only when we're actually recovering, otherwise let
 * the real failure surface instead of leaving a blank Suspense fallback.
 */
function attemptReload(): boolean {
  if (recentlyReloaded()) return false
  try {
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()))
  } catch {
    // Private mode / storage disabled — still attempt the reload below; the
    // worst case without the stamp is one extra reload, not an infinite loop
    // (a stale chunk resolves after the first reload to the fresh shell).
  }
  window.location.reload()
  return true
}

/**
 * Register the chunk-load recovery listeners. Call once at app bootstrap,
 * before React renders. No-op outside the browser.
 */
export function installChunkReload(): void {
  if (typeof window === 'undefined') return

  // Vite's first-class hook for a failed dynamic-import preload. preventDefault
  // suppresses Vite's rethrow of the import error — only do that when a reload
  // is actually happening, so a broken deploy (guard blocks the reload) surfaces
  // the real error instead of leaving the route on a blank Suspense fallback.
  window.addEventListener('vite:preloadError', (event) => {
    if (attemptReload()) event.preventDefault()
  })

  // Fallback: a failed dynamic import usually surfaces as a rejected promise.
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason as { message?: string } | string | undefined
    const message = typeof reason === 'string' ? reason : reason?.message
    if (isChunkLoadErrorMessage(message)) attemptReload()
  })

  // Belt-and-suspenders: some runtimes emit a plain error event instead.
  window.addEventListener('error', (event) => {
    if (isChunkLoadErrorMessage(event.message)) attemptReload()
  })
}
